import React, {ChangeEventHandler, useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {SwitcherLabel} from 'src/components/switcher-label';
import {useAdminAccessRight} from 'src/hooks/access-rights';

import {Dialog} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import StateButton from '@pb/kit-component-state-button';
import Typography from '@pb/kit-component-typography';

import {ChangesInfo} from '../../../../components/changes-info';
import {ConfirmationModal} from '../components/confirmation-modal';
import {KeyControl} from '../components/key-control';
import {CONFIRM} from '../constants';
import {useConfirmActions, useError} from '../hooks';
import {setupDataSelector} from '../services/selectors';

import type {ConfirmTypes} from '../constants';
import type {Settings} from 'src/openapi/admin/models/Settings';


export const FULL_ACCESS_NAME = 'full.access';

export const APIKeySection = () => {
    const params = useParams<{accountId: string}>();
    const accountId = params.accountId ? +params.accountId : -1;

    const {canEditApiV2} = useAdminAccessRight();
    const data: Settings | null = useSelector(setupDataSelector);
    const {error} = useError();
    const {onToggleApi, onToggleCrmIntegration, onConfirm} = useConfirmActions();

    const [confirmType, setConfirmType] = useState<ConfirmTypes | null>(null);

    const showConfirmModal = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const value = event.currentTarget.checked;

        if (!value) {
            setConfirmType(CONFIRM.DISABLE_FULL_ACCESS);
        } else {
            onConfirm(CONFIRM.DISABLE_FULL_ACCESS, value);
        }
    }, [onConfirm]);

    const onClose = useCallback(() => {
        setConfirmType(null);
    }, []);

    if (!data) {
        return null;
    }

    const {enabled, apiKey, fullAccess, devKey, changesInfo, crmIntegration, crmIntegrationEditable} = data;

    const updateApiV2Disabled = !canEditApiV2(accountId);
    const updateFullAccessDisabled = !enabled || updateApiV2Disabled;
    const updateCrmIntegrationDisabled = !crmIntegrationEditable || updateApiV2Disabled;
    const generateAPIKeyDisabled = !enabled || !fullAccess || updateApiV2Disabled;
    const generateSandboxKeyDisabled = !enabled || updateApiV2Disabled;

    return (
        <>
            {confirmType ? (
                <Dialog
                    shown={!!confirmType}
                    setShown={value => {
                        if (!value) {
                            onClose();
                        }
                    }}>
                    <ConfirmationModal {...{confirmType, onClose}}/>
                </Dialog>
            ) : null}
            <Group direction="v" gap="xl">
                <Typography
                    fontWeight="semi-bold"
                    variant="heading-level-3">
                    API Key
                </Typography>
                <Group direction="v" gap="l">
                    <SwitcherLabel caption="API">
                        <StateButton
                            {...{
                                name: 'api.v2',
                                disabled: updateApiV2Disabled,
                                checked: enabled,
                                onChange: onToggleApi,
                                key: Number(!!error),
                            }}
                        />
                    </SwitcherLabel>
                    <SwitcherLabel caption="Full Access">
                        <StateButton
                            {...{
                                name: FULL_ACCESS_NAME,
                                disabled: updateFullAccessDisabled,
                                checked: fullAccess,
                                onChange: showConfirmModal,
                                key: Number(!!error || confirmType === CONFIRM.DISABLE_FULL_ACCESS),
                            }}
                        />
                    </SwitcherLabel>
                    <SwitcherLabel caption="CRM Integration">
                        <StateButton
                            {...{
                                name: 'crm.integration',
                                disabled: updateCrmIntegrationDisabled,
                                checked: crmIntegration,
                                onChange: onToggleCrmIntegration,
                                key: Number(!!error),
                            }}
                        />
                    </SwitcherLabel>
                </Group>
                <KeyControl
                    {...{
                        label: 'API Key',
                        keyValue: apiKey || '',
                        disabled: generateAPIKeyDisabled,
                        confirmType: CONFIRM.GENERATE_API_KEY,
                        onGenerateKey: setConfirmType,
                    }}
                />
                <KeyControl
                    {...{
                        label: 'Sandbox Key',
                        keyValue: devKey || '',
                        disabled: generateSandboxKeyDisabled,
                        confirmType: CONFIRM.GENERATE_SANDBOX_KEY,
                        onGenerateKey: setConfirmType,
                    }}
                />
                <ChangesInfo data={changesInfo}/>
            </Group>
        </>
    );
};
